export type FooterLink = {
  text: string;
  url?: string;
  type?: "privacy";
};

export type FooterColumn = {
  title: string;
  links: FooterLink[];
};

export type FooterSocial = {
  name: "facebook" | "instagram" | "youtube" | "tiktok";
  url: string;
};

export type MainFooterContent = {
  columns: FooterColumn[];
  socials: FooterSocial[];
};

const sharedFooterColumnTitles = [
  "Company",
  "Support",
  "Get in touch",
] as const;

const sharedSocials: FooterSocial[] = [
  {
    name: "facebook",
    url: "https://www.facebook.com/groups/initofertility",
  },
  {
    name: "instagram",
    url: "https://www.instagram.com/initofertility",
  },
  {
    name: "youtube",
    url: "https://www.youtube.com/@Inito",
  },
  {
    name: "tiktok",
    url: "https://www.tiktok.com/@initofertility",
  },
];

const sharedColumns: FooterColumn[] = [
  {
    title: sharedFooterColumnTitles[0],
    links: [
      { text: "About Us", url: "/about-us" },
      { text: "Buy Now", url: "/buy-now" },
      { text: "Blog", url: "https://blog.inito.com/" },
      { text: "Our Medical Advisors", url: "/medical-advisors" },
    ],
  },
  {
    title: sharedFooterColumnTitles[1],
    links: [
      { text: "FAQ", url: "/faqs" },
      { text: "Terms", url: "/terms" },
      { text: "Privacy Policy", url: "/privacy-policy" },
      {
        text: "Cancellation, Return & Warranty",
        url: "/replacements-and-warranty",
      },
      {
        text: "Consumer Health Data Privacy Policy",
        url: "/consumer-health-data-privacy-policy",
      },
      { text: "Your Privacy Choices", type: "privacy" },
    ],
  },
];

export const defaultFooterContent: MainFooterContent = {
  columns: [
    ...sharedColumns,
    {
      title: sharedFooterColumnTitles[2],
      links: [
        { text: "+91-9742931931", url: "tel:+919742931931" },
        { text: "help@inito.com", url: "mailto:help@inito.com" },
        {
          text: "1st Floor, #57, Indiqube Infinia, Koramangala Extension, Bengaluru, 560034, IN",
        },
      ],
    },
  ],
  socials: sharedSocials,
};

export const usFooterContent: MainFooterContent = {
  columns: [
    ...sharedColumns,
    {
      title: sharedFooterColumnTitles[2],
      links: [
        { text: "+1 815-369-0989", url: "tel:+18153690989" },
        { text: "help@inito.com", url: "mailto:help@inito.com" },
        {
          text: "24 Greenway Plaza, Suite 1800, Houston, TX, USA 77046",
        },
      ],
    },
  ],
  socials: sharedSocials,
};

export const mainFooterContent = usFooterContent;

/** Shared legal footnotes shown above footer columns (footnote 1 site-wide; all three on Buy Now). */
export const FOOTER_FOOTNOTE_1 =
  '1: "Most precise" refers to system-level precision measured in controlled laboratory testing, based on lower between-device variability across key hormones, concentrations, operators, days, and strip lots. Compares Inito to other U.S. over-the-counter at-home fertility monitors that provide numeric or graded hormone values and measure the same hormones as Inito.';

/** Suffix of FOOTER_FOOTNOTE_1 after which a desktop-only line break is inserted (after “days, and ”). */
export const FOOTER_FOOTNOTE_1_DESKTOP_BREAK_AFTER =
  "operators, days, and ";

export const FOOTER_FOOTNOTE_2 =
  "2: Validation of urinary reproductive hormone measurements using a novel smartphone connected reader. Scientific Reports (Nature), 2023";

export const FOOTER_FOOTNOTE_3 =
  "3: Predicting serum hormone concentration by estimation of urinary hormones through a home-use device. Human Reproduction Open (Oxford University Press / ESHRE), 2023";

export const defaultFooterFootnotes: readonly string[] = [FOOTER_FOOTNOTE_1];

export const buyNowFooterFootnotes: readonly string[] = [
  FOOTER_FOOTNOTE_1,
  FOOTER_FOOTNOTE_2,
  FOOTER_FOOTNOTE_3,
];
